import React, { useRef, useState } from 'react';
import { api, formatApiError, resolveImageUrl } from '../../lib/api';

/**
 * ImageUploader — uploads a file to /api/admin/upload and reports back the
 * returned URL via onUploaded(url). Also accepts a current value (URL) to
 * preview in place.
 */
const ImageUploader = ({ value, onUploaded, label = 'Image', testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(data.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onClear = () => {
    onUploaded('');
    setError('');
  };

  const preview = value ? resolveImageUrl(value) : '';

  return (
    <div data-testid={testId}>
      <span className="block text-xs font-light tracking-wider uppercase text-gray-500 mb-2">
        {label}
      </span>

      <div className="flex items-start gap-4">
        <div className="w-40 h-28 bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-light text-gray-400">Aperçu</span>
          )}
        </div>

        <div className="flex-1">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onUploaded(e.target.value)}
            placeholder="Cliquez sur 'Choisir un fichier' ou collez une URL https://..."
            className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
            data-testid={testId ? `${testId}-url` : undefined}
          />
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={onPick}
              disabled={uploading}
              className="text-xs font-light tracking-wider uppercase bg-black text-white px-4 py-2 hover:bg-[#d94e47] transition-colors disabled:opacity-50"
              data-testid={testId ? `${testId}-pick` : undefined}
            >
              {uploading ? 'Envoi...' : 'Choisir un fichier'}
            </button>
            {value && (
              <button
                type="button"
                onClick={onClear}
                className="text-xs font-light tracking-wider uppercase text-gray-500 hover:text-[#d94e47]"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                Retirer
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onChange}
            className="hidden"
          />
          {error && (
            <p className="mt-2 text-xs font-light text-[#d94e47]">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
